import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { Suspense, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import ProductDetailPage from '../pages/ProductDetailPage';
import { productRegistry } from '../routes/productRegistry';
import { track } from '../utils/analytics';
import { transport } from '../utils/transport';
const productClient = createClient(ProductService, transport);
function ProductLoadingSkeleton() {
    return /*#__PURE__*/ _jsx("div", {
        className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-pulse",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "grid grid-cols-1 lg:grid-cols-2 gap-12",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "aspect-[4/5] bg-stone-100"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-8 bg-stone-100 w-3/4"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-6 bg-stone-100 w-1/4"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-20 bg-stone-100 w-full"
                        })
                    ]
                })
            ]
        })
    });
}
/**
 * Dispatcher for every `/shop/:slug` URL.
 *
 * 1. Fetches the product row once via `ProductService.GetProduct` (cached by react-query).
 * 2. Fires `view_product` analytics once per mount/slug.
 * 3. If `productRegistry[slug]` has a bespoke component, renders it lazy-loaded inside a
 *    Suspense boundary.
 * 4. Otherwise renders the generic `ProductDetailPage` fallback.
 * 5. 404s uniformly when the slug doesn't resolve to a product.
 */ export default function ProductRoute() {
    const { slug } = useParams();
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'product',
            slug
        ],
        queryFn: ()=>productClient.getProduct({
                slug: slug ?? '',
                productId: ''
            }),
        enabled: !!slug
    });
    const product = data?.product;
    useEffect(()=>{
        if (!product) return;
        track({
            type: 'view_product',
            payload: {
                itemId: product.id,
                itemName: product.name,
                priceCents: Number(product.priceCents),
                slug: product.slug
            }
        });
    }, [
        product
    ]);
    if (isLoading) return /*#__PURE__*/ _jsx(ProductLoadingSkeleton, {});
    if (isError || !product) return /*#__PURE__*/ _jsx(Navigate, {
        to: "/shop",
        replace: true
    });
    const Bespoke = slug ? productRegistry[slug] : undefined;
    if (Bespoke) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(ProductLoadingSkeleton, {}),
            children: /*#__PURE__*/ _jsx(Bespoke, {
                product: product
            })
        });
    }
    return /*#__PURE__*/ _jsx(ProductDetailPage, {
        product: product
    });
}
